import { Fingerprint } from "lucide-react";
import type { Instrument } from "../data/instruments";
import { getTuningById, getVariantById } from "../data/instruments";
import { useI18n } from "../i18n/LocaleContext";
import { useTimbroamState } from "../lib/appState";
import { CulturalCautionPanel } from "./CulturalCautionPanel";
import { TuningWarningBadge } from "./TuningWarningBadge";

interface SoundDnaPanelProps {
  instrument: Instrument;
  testIdPrefix?: string;
}

export function SoundDnaPanel({ instrument, testIdPrefix = "" }: SoundDnaPanelProps) {
  const { state, dispatch } = useTimbroamState();
  const { t, getInstrumentText } = useI18n();
  const instrumentText = getInstrumentText(instrument);
  const selectedVariant = getVariantById(instrument.id, state.selectedVariantId);
  const selectedTuning = getTuningById(instrument.id, state.selectedTuningId);
  const sampleLibraryPending = selectedVariant.sampleLayers.every((layer) => !layer.samplePath);
  const withPrefix = (id: string) => (testIdPrefix ? `${testIdPrefix}-${id}` : id);
  const highlightControl = (targetId: string) => {
    if (typeof document === "undefined") {
      return;
    }

    const target = document.querySelector<HTMLElement>(`[data-control-target="${targetId}"]`);

    if (!target) {
      return;
    }

    target.scrollIntoView({ block: "center", behavior: "smooth" });
    target.classList.add("is-control-highlighted");
    window.setTimeout(() => target.classList.remove("is-control-highlighted"), 1200);
  };
  const controlActionsByInstrument: Record<string, Array<{ label: string; targetId: string }>> = {
    gayageum: [
      { label: "Nonghyeon zone", targetId: "gayageum-nonghyeon" },
      { label: "Pluck zone", targetId: "gayageum-pluck" }
    ],
    erhu: [
      { label: "Bow pressure", targetId: "erhu-bow-pressure" },
      { label: "Portamento drag", targetId: "erhu-portamento" }
    ],
    oud: [
      { label: "Course pairs", targetId: "oud-courses" },
      { label: "Microbend", targetId: "oud-microbend" }
    ],
    sitar: [
      { label: "Meend range", targetId: "sitar-meend" },
      { label: "Chikari", targetId: "sitar-chikari" }
    ],
    daegeum: [
      { label: "Cheong buzz", targetId: "daegeum-cheong" },
      { label: "Breath", targetId: "daegeum-breath" }
    ],
    ney: [
      { label: "Breath edge", targetId: "ney-breath" },
      { label: "Overblow", targetId: "ney-overblow" }
    ],
    janggu: [
      { label: "Head zones", targetId: "janggu-heads" },
      { label: "Jangdan", targetId: "janggu-jangdan" }
    ],
    tabla: [
      { label: "Bayan pressure", targetId: "tabla-bayan-pressure" },
      { label: "Dayan zones", targetId: "tabla-dayan-zones" }
    ],
    mbira: [
      { label: "Manual registers", targetId: "mbira-manuals" },
      { label: "Buzz layer", targetId: "mbira-buzz" }
    ],
    kora: [
      { label: "Left/right ranks", targetId: "kora-ranks" },
      { label: "Ostinato", targetId: "kora-ostinato" }
    ],
    yidaki_didgeridoo: [
      { label: "Mouth shape", targetId: "didgeridoo-mouth" },
      { label: "Drone tube", targetId: "didgeridoo-drone" }
    ],
    gamelan_saron: [
      { label: "Bronze bars", targetId: "saron-bars" },
      { label: "Ombak", targetId: "saron-ombak" }
    ]
  };
  const controlActions = controlActionsByInstrument[instrument.id] ?? [];
  const entries = [
    [t.soundDna.family, `${instrumentText.family} (${instrument.classification.family})`],
    [t.soundDna.attack, instrument.soundDna.attack],
    [t.soundDna.sustain, instrument.soundDna.sustain],
    [t.soundDna.resonance, instrument.soundDna.resonance],
    [t.soundDna.ornament, instrument.soundDna.ornamentation],
    [t.soundDna.identity, instrument.soundDna.identity]
  ];

  return (
    <aside className="sound-dna-panel" data-testid={withPrefix("sound-dna-panel")} aria-labelledby={withPrefix("sound-dna-title")}>
      <div className="panel-heading">
        <div>
          <p className="eyebrow">{t.soundDna.eyebrow}</p>
          <h2 id={withPrefix("sound-dna-title")}>{t.soundDna.title}</h2>
        </div>
        <Fingerprint size={22} aria-hidden="true" />
      </div>

      <section className="dna-summary-card" aria-label={t.soundDna.compactLabel}>
        <p>{instrument.soundDna.identity}</p>
        <div className="insight-action-row" aria-label={t.soundDna.controlsLabel}>
          {controlActions.map((action) => (
            <button key={action.targetId} type="button" onClick={() => highlightControl(action.targetId)}>
              {action.label}
            </button>
          ))}
        </div>
      </section>

      <details className="insight-accordion" aria-label={t.soundDna.detailsHeading}>
        <summary>{t.soundDna.detailsHeading}</summary>
        <dl className="dna-list">
          {entries.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </details>

      <details className="insight-accordion" data-testid={withPrefix("variant-summary")} aria-label={t.soundDna.variantInfo}>
        <summary>{t.soundDna.variantInfo}</summary>
        <section className="variant-summary" aria-label="Selected variant details">
          <p className="eyebrow">{t.soundDna.variant}</p>
          <h3>{selectedVariant.name}</h3>
          <p>{selectedVariant.toneProfile.description}</p>
          <dl>
            <div>
              <dt>{t.soundDna.construction}</dt>
              <dd>{selectedVariant.construction}</dd>
            </div>
            <div>
              <dt>{t.soundDna.controls}</dt>
              <dd>{selectedVariant.controls.map((control) => control.label).join(", ")}</dd>
            </div>
            <div>
              <dt>{t.soundDna.samples}</dt>
              <dd>{sampleLibraryPending ? t.soundDna.samplePending : t.soundDna.sampleAvailable}</dd>
            </div>
          </dl>
        </section>
      </details>

      <details className="insight-accordion" aria-label={t.soundDna.tuningWarning}>
        <summary>{t.soundDna.tuningWarning}</summary>
        <section className="tuning-summary" aria-label="Selected tuning details">
          <p className="eyebrow">{t.soundDna.tuning}</p>
          <h3>{selectedTuning.name}</h3>
          <p>{selectedTuning.description}</p>
          <TuningWarningBadge tuning={selectedTuning} testId={withPrefix("warning-badge")} />
        </section>
      </details>

      {state.culturalWarningVisible ? (
        <CulturalCautionPanel
          title={instrumentText.culturalCaution ? t.soundDna.respectNote : t.soundDna.culturalContext}
          message={instrumentText.culturalCaution ?? instrumentText.culturalNote}
          testId={withPrefix("cultural-caution-panel")}
          onDismiss={() => dispatch({ type: "setCulturalWarningVisible", visible: false })}
        />
      ) : null}
    </aside>
  );
}
